import { isNumeric } from '../../util/helpers';

import { LatLon } from '../../util/geodesy/latlon';
import { ReviewSummary } from '../../shared/models/review-summary.model';
import { ApproachType } from './approach-type.model';
import { AirportFuelSummary } from './airport-fuel-summary.model';
import { parseLatLon } from '../util/parseUtils';
import { Airport, AirportOwnershipType, AirportStatus, AirportType } from './airport.model';


/**
 * Model for airport search result. This class contains basic information about the airport, and just enough
 * information to enable client-side filtering on supported search criteria.
 */
export class AirportSearchResult {
  /**
   * Numeric identifier that uniquely identifies this airport in the database
   */
  airportId: number;

  /**
   * 3- or 4-letter FAA identifier of the airport (if available)
   */
  faaId: string;

  /**
   * 4-letter ICAO identifier of the airport (if available)
   */
  icaoId: string;

  /**
   * Gets the identifier that should be displayed for this airport. This will either be the ICAO identifier if
   * available, or the FAA identifier otherwise.
   */
  get identifier(): string {
    return this.icaoId || this.faaId || '';
  }

  /**
   * Airport Type
   */
  type: AirportType;

  /**
   * Airport ownership type (e.g., publicly vs private owned, or whether it's owned by army, navy, air force, or coast
   * guard). Null if no data available.
   */
  airportOwnership: AirportOwnershipType;

  /**
   * Airport operational status
   */
  status: AirportStatus;

  /**
   * Airport name
   */
  name: string;

  /**
   * Associated city being served by this airport (note: not necessarily the same city where the airport is physically
   * located).
   */
  city: string;

  /**
   * State or province abbreviation of city being served by this airport (note: not necessarily the same state where the
   * airport is physically located).
   */
  stateProvince: string;

  /**
   * Associated country.
   */
  country: string;

  /**
   * Indicates whether this airport is public use (null if no data).
   */
  publicUse: boolean;

  /**
   * Indicates whether airport has military/civil joint use agreement that allows civil operations at a military airport
   * or military operations at a civil airport (null if data is not available).
   */
  militaryJoint: boolean;

  /**
   * Airport latitude and longitude
   */
  location: LatLon;

  /**
   * Distance of airport from center of search area
   */
  distance: number;

  /**
   * Whether the airport has an operating control tower
   */
  towered: boolean;

  /**
   * Longest runway length at airport
   */
  maxRunwayLength: number;

  /**
   * Summary object of reviews for this entity
   */
  reviewSummary: ReviewSummary;

  /**
   * Indicates whether this airport has been designated by the U.S. Treasury Department as an International Airport of
   * Entry (AOE) for customs (null if data is not available).
   */
  customsIntlEntry: boolean;

  /**
   * Rental car availability (any brand)
   */
  rentalCar: boolean;

  /**
   * Availability of oxygen (bulk or bottled)
   */
  oxygen: boolean;

  /**
   * Instrument approach procedure types available at airport
   */
  approaches: Array<ApproachType>;

  /**
   * Airport fuel availability
   */
  fuel: Array<AirportFuelSummary>;

  /**
   * If true, airport has at least one paved runway.
   */
  paved: boolean;

  /**
   * If true, airport has at least one helipad.
   */
  helipad: boolean;

  /**
   * If true, airport has at least one waterway.
   */
  waterway: boolean;

  /**
   * If true, airport has at least one grass runway.
   */
  grass: boolean;

  /**
   * If true, airport has airframe repair services.
   */
  airframeRepair: boolean;

  /**
   * If true, airport has engine repair services.
   */
  engineRepair: boolean;

  /**
   * If true, airport has avionics repair services.
   */
  avionicsRepair: boolean;

  /**
   * Airport has a restaurant on the field
   */
  restaurantOnField: boolean;

  /**
   * Airport has camping on field
   */
  campingOnField: boolean;

  /**
   * Courtesy car availability
   */
  courtesyCar: boolean;

  /**
   * If true, airport has a landing fee
   */
  landingFee: boolean;

  /**
   * Used for showing prices on the map, (Low, Med, or High) based on lowest price for filtered fuel type selected at this airport
   */
  minPriceType: string; // Should this be an enum?

  /**
   * Used for showing prices on the map, the actual price that will be shown as a label to the map marker
   */
  minPrice: number;

  /**
   * The airport source Id
   */
  sourceId: number;

  /**
   * Parses an airport search result as returned by the API into an instance of the AirportSearchResult class.
   */
  static parse(data: any): AirportSearchResult {
    return Object.assign(new AirportSearchResult(), {
      airportId: data.airportId,
      faaId: data.faaId,
      icaoId: data.icaoId,
      type: Airport.parseAirportType(data.type),
      airportOwnership: Airport.parseAirportOwnershipType(data.airportOwnership),
      status: Airport.parseAirportStatus(data.status),
      name: data.name,
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      country: data.country || null,
      publicUse: Airport.parsePublicUse(data.airportUsage),
      militaryJoint: data.militaryJoint,
      location: data.location ? parseLatLon(data.location.latitude, data.location.longitude) : null,
      distance: isNumeric(data.distance) ? +data.distance : null,
      towered: data.towerPresent,
      maxRunwayLength: isNumeric(data.maxRunwayLength) ? +data.maxRunwayLength : null,
      reviewSummary: data.reviewSummary ? ReviewSummary.parse(data.reviewSummary) : null,
      customsIntlEntry: data.customsIntlEntry,
      rentalCar: data.rentalCars,
      oxygen: data.oxygen,
      approaches: AirportSearchResult.parseApproaches(data.approaches),
      fuel: (data.fuel || []).map(f => AirportFuelSummary.parse(f)),
      paved: data.paved,
      helipad: data.helipad,
      waterway: data.waterway,
      grass: data.grass,
      airframeRepair: data.airframeRepair,
      engineRepair: data.engineRepair,
      avionicsRepair: data.avionicsRepair,
      restaurantOnField: data.restaurantOnField,
      campingOnField: data.campingOnField,
      courtesyCar: data.courtesyCar,
      landingFee: data.landingFee,
      minPriceType: null,
      minPrice: null,
      sourceId: data.sourceId
    });
  }

  /**
   * Parses approaches available at airport
   * @param approaches {Array<string>} Approaches as returned by API
   * @return {Array<ApproachType>} Parsed approach types
   */
  static parseApproaches(approaches: any): Array<ApproachType> {
    if (!approaches || approaches.length < 1) {
      return [];
    }
    return (approaches || []).map(apch => {
      switch (apch) {
        case 'ILS':
          return ApproachType.ILS;
        case 'RNAV':
          return ApproachType.RNAV;
        case 'VOR':
          return ApproachType.VOR;
        default:
          return null;
      }
    }).filter(v => v);
  }
}
